import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, UserCircle, Calendar, Clock, FileText, AlertTriangle,
  TrendingUp, TrendingDown, CheckCircle, AlertCircle, DollarSign,
  ArrowRight, Activity, Zap, ShieldCheck, Sparkles, RefreshCw,
  Navigation2, Bell, Heart, ClipboardCheck, CreditCard,
  Cake, UserPlus, ChevronRight, FileWarning, X,
  Loader2, Flame, HeartPulse, CalendarRange, BookOpen,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, differenceInDays } from 'date-fns';
import clsx from 'clsx';
import AttendanceAlerts from '../components/AttendanceAlerts';

// ── Animation ────────────────────────────────────────────────────────────────
const container = { hidden: {}, show: { transition: { staggerChildren: .05, delayChildren: .02 } } };
const fadeUp    = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: .3, ease: [.4,0,.2,1] } } };
const fadeIn    = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: .3 } } };

// ── Animated counter ──────────────────────────────────────────────────────────
function AnimatedNumber({ value, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);
  const target = parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
  const isFloat = String(value).includes('.');
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 700, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(e * target);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  const f = isFloat
    ? display.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : Math.round(display).toLocaleString();
  return <span>{prefix}{f}{suffix}</span>;
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, color, bg, to, alert }) {
  const inner = (
    <motion.div variants={fadeUp} whileHover={{ y:-2, transition:{duration:.15} }}
      className={clsx('relative rounded-2xl p-4 border overflow-hidden group cursor-pointer', bg)}>
      {alert && <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
      <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center mb-3', color + '/15')}>
        <Icon size={17} className={color} />
      </div>
      <p className={clsx('text-2xl font-black leading-none', color)}>
        <AnimatedNumber value={typeof value === 'string' ? value.replace(/[^0-9.]/g,'') : (value || 0)}
          prefix={typeof value === 'string' && value.startsWith('$') ? '$' : ''}
          suffix={typeof value === 'string' && value.endsWith('%') ? '%' : ''} />
      </p>
      <p className="text-xs font-semibold text-slate-600 mt-1.5">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </motion.div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

// ── Circular gauge ────────────────────────────────────────────────────────────
function Gauge({ value, size = 120, strokeWidth = 10 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  const color = value >= 70 ? '#10b981' : value >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 1s ease' }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        fill={color} fontSize={size/4} fontWeight="800"
        style={{ transform:`rotate(90deg) translateX(0px)`, transformOrigin:`${size/2}px ${size/2}px` }}>
        {value}%
      </text>
    </svg>
  );
}

// ── Health bar ────────────────────────────────────────────────────────────────
function HealthBar({ label, pct, target }) {
  const color = pct >= (target || 70) ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <motion.div className="h-2 rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: .9, ease: [.4,0,.2,1] }} />
      </div>
    </div>
  );
}

// ── Activity badge ────────────────────────────────────────────────────────────
function ActivityBadge({ type, status }) {
  if (type === 'incident') return <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">Incident</span>;
  if (status === 'completed') return <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Completed</span>;
  if (status === 'cancelled') return <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-bold">Cancelled</span>;
  return <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Shift</span>;
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    booked:'bg-blue-100 text-blue-700', completed:'bg-emerald-100 text-emerald-700',
    cancelled:'bg-red-100 text-red-500', pending:'bg-amber-100 text-amber-700',
    invoiced:'bg-violet-100 text-violet-700', vacant:'bg-orange-100 text-orange-700',
  };
  return <span className={clsx('text-[10px] font-bold px-2 py-0.5 rounded-full capitalize', map[status] || 'bg-slate-100 text-slate-500')}>{status}</span>;
}

// ── Chart tooltip ─────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-slate-600 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-bold text-slate-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const AVATAR_COLORS = ['#6366f1','#10b981','#3b82f6','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
function Avatar({ name = '?', i = 0, size = 7 }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className={`w-${size} h-${size} rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0`}
      style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
      {initials}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight]   = useState(null);
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiError, setAiError]       = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [aiInsights, setAiInsights] = useState(null);
  const [aiCenterLoading, setAiCenterLoading] = useState(false);

  useEffect(() => {
    api.get('/dashboard')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fetchInsight = (stats) => {
    setAiLoading(true); setAiError(false);
    api.post('/ai/insights', { stats, scope: 'dashboard' })
      .then(r => setAiInsight(r.data.insight))
      .catch(() => setAiError(true))
      .finally(() => setAiLoading(false));
  };

  useEffect(() => {
    if (!data) return;
    fetchInsight({
      shiftsToday: data.shiftsToday, pendingTimesheets: data.pendingTimesheets,
      openIncidents: data.openIncidents, overdueInvoices: data.overdueInvoices,
      expiringDocs: data.expiringDocs, activeClients: data.activeClients,
      activeStaff: data.activeStaff, revenueThisMonth: data.revenueThisMonth,
      healthScore: data.healthScore,
    });
  }, [data]);

  const runAiAnalysis = async () => {
    setAiCenterLoading(true);
    try {
      const now   = new Date();
      const end   = now.toISOString().split('T')[0];
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30).toISOString().split('T')[0];
      const [burnout, funding, deterioration] = await Promise.allSettled([
        // Both scans are POST on the server (they run an AI review); a GET here always failed.
        api.post('/ai/staff-burnout'),
        api.get(`/reports/plan-managed-funding?start_date=${start}&end_date=${end}`),
        api.post('/ai/client-deterioration'),
      ]);
      const rawFunding = funding.status === 'fulfilled' ? (funding.value.data?.clients || funding.value.data || []) : [];
      const atRiskFunds = rawFunding.filter(f => {
        const spent = parseFloat(f.spent_amount || 0);
        const total = parseFloat(f.fund_amount  || 0);
        return total > 0 && (spent / total) > 0.80;
      });
      setAiInsights({
        burnout:      burnout.status      === 'fulfilled' ? (burnout.value.data?.atRisk || []) : null,
        funding:      atRiskFunds,
        fundingError: funding.status      !== 'fulfilled',
        deterioration: deterioration.status === 'fulfilled' ? (deterioration.value.data?.alerts || []) : null,
        deteriorationError: deterioration.status !== 'fulfilled',
        burnoutError: burnout.status !== 'fulfilled',
      });
    } catch {
      setAiInsights({ burnoutError: true, fundingError: true, deteriorationError: true, funding: [] });
    }
    setAiCenterLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        <p className="text-sm text-slate-400 font-medium">Loading dashboard…</p>
      </div>
    </div>
  );
  if (!data) return null;

  const {
    shiftsToday: st, shiftsThisWeek, activeClients, activeStaff, pendingTimesheets,
    openIncidents, overdueInvoices, revenueThisMonth, expiringDocs,
    shiftsChart, recentShifts, staffWorkload, upcomingReviews,
    kmToday, kmWeek, incidentBreakdown = [], leavePending = 0,
    birthdaysThisMonth = [], clientStats = {}, progressNotes = {},
    invoiceHealth = {}, staffOnboarding = {}, clientDocsCount = 0,
    newClients = [], activityFeed = [], expiringClientDocs = [],
    healthScore = {}, finSummary = {}, finLastMonth = {}, topClients = [],
  } = data;

  const chartData = shiftsChart.map(d => ({
    date: format(parseISO(d.date), 'EEE d'),
    Scheduled: parseInt(d.shifts),
    Completed: parseInt(d.completed),
    Cancelled: parseInt(d.cancelled || 0),
  }));

  // Incident breakdown map
  const incMap = incidentBreakdown.reduce((m, r) => { m[r.status] = parseInt(r.count); return m; }, {});
  const totalInc = Object.values(incMap).reduce((s, v) => s + v, 0);

  // Progress notes
  const totalCompletedShifts = parseInt(progressNotes.total_completed || 0);
  const withNotesCount       = parseInt(progressNotes.with_notes || 0);

  // Shift status donut data
  const shiftDonut = [
    { name: 'Completed', value: parseInt(st.completed||0), color: '#10b981' },
    { name: 'Booked',    value: parseInt(st.booked||0),    color: '#6366f1' },
    { name: 'Vacant',    value: parseInt(st.vacant||0),    color: '#f59e0b' },
    { name: 'Cancelled', value: parseInt(st.cancelled||0), color: '#ef4444' },
  ].filter(d => d.value > 0);

  // Overdue amount
  const overdueAmt = parseFloat(overdueInvoices?.amount || 0);

  // Active alerts
  const alerts = [
    overdueAmt > 0 && { id:'inv', icon: DollarSign, color:'text-red-600', bg:'bg-red-50 border-red-100',
      msg: `${overdueInvoices?.overdue || 0} overdue invoices — $${overdueAmt.toLocaleString()} outstanding`, to:'/invoices' },
    expiringDocs > 0 && { id:'doc', icon: FileWarning, color:'text-amber-600', bg:'bg-amber-50 border-amber-100',
      msg: `${expiringDocs} staff documents expiring within 30 days`, to:'/reports/staff-document-expiry' },
    leavePending > 0 && { id:'leave', icon: Clock, color:'text-blue-600', bg:'bg-blue-50 border-blue-100',
      msg: `${leavePending} leave ${leavePending === 1 ? 'request' : 'requests'} awaiting approval`, to:'/leave' },
    upcomingReviews?.length > 0 && { id:'rev', icon: AlertCircle, color:'text-violet-600', bg:'bg-violet-50 border-violet-100',
      msg: `${upcomingReviews.length} client ${upcomingReviews.length===1?'review':'reviews'} due in the next 14 days`, to:'/clients' },
  ].filter(Boolean).filter(a => !dismissedAlerts.has(a.id));

  const today = new Date();
  const greeting = today.getHours() < 12 ? 'Good morning' : today.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-5">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{greeting} 👋</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {format(today, 'EEEE, d MMMM yyyy')}{user?.orgName ? ` · ${user.orgName}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-full font-medium">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />Live
          </span>
          <Link to="/reports/hub" className="text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full font-medium hover:bg-slate-200 transition-colors">
            Reports Hub
          </Link>
          <Link to="/scheduler" className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded-full font-medium hover:bg-primary-700 transition-colors">
            + New Shift
          </Link>
        </div>
      </motion.div>

      {/* ── Alerts ──────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {alerts.length > 0 && (
          <motion.div variants={fadeUp} className="space-y-2">
            {alerts.map(a => (
              <motion.div key={a.id} initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }}
                className={clsx('flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm', a.bg)}>
                <div className="flex items-center gap-2.5">
                  <a.icon size={14} className={clsx('shrink-0', a.color)} />
                  <span className={a.color}>{a.msg}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.to && (
                    <Link to={a.to} className={clsx('text-xs font-semibold flex items-center gap-1', a.color)}>
                      View <ChevronRight size={11} />
                    </Link>
                  )}
                  <button onClick={() => setDismissedAlerts(s => new Set([...s, a.id]))}
                    className="text-slate-400 hover:text-slate-600">
                    <X size={13} />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Clock-in / clock-out alerts: forgot to clock out, never clocked in ── */}
      <motion.div variants={fadeUp}>
        <AttendanceAlerts />
      </motion.div>

      {/* ── KPI Cards ───────────────────────────────────────────────────────── */}
      <motion.div variants={container} className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard icon={Calendar}      label="Today's Shifts"     value={st.total || 0}
          sub={`${st.completed||0} completed`} color="text-indigo-600" bg="bg-indigo-50 border-indigo-100" to="/scheduler" />
        <KpiCard icon={Users}         label="Active Clients"     value={activeClients || 0}
          sub={`${clientStats.new_this_month||0} new this month`} color="text-emerald-600" bg="bg-emerald-50 border-emerald-100" to="/clients" />
        <KpiCard icon={UserCircle}    label="Active Staff"       value={activeStaff || 0}
          sub={`${staffOnboarding.total||0} in system`} color="text-blue-600" bg="bg-blue-50 border-blue-100" to="/staff" />
        <KpiCard icon={Clock}         label="Pending Timesheets" value={pendingTimesheets || 0}
          sub="awaiting approval" color="text-amber-600" bg="bg-amber-50 border-amber-100" to="/timesheets"
          alert={pendingTimesheets > 0} />
        <KpiCard icon={AlertTriangle} label="Open Incidents"    value={openIncidents || 0}
          sub={`${incMap.closed||0} closed`} color="text-red-600" bg="bg-red-50 border-red-100" to="/incidents"
          alert={openIncidents > 0} />
        <KpiCard icon={DollarSign}    label="Revenue (MTD)"
          value={`$${Math.round(revenueThisMonth||0).toLocaleString()}`}
          sub="paid invoices" color="text-violet-600" bg="bg-violet-50 border-violet-100" to="/invoices" />
      </motion.div>

      {/* ── AI Insight ──────────────────────────────────────────────────────── */}
      <motion.div variants={fadeUp}
        className="rounded-2xl border border-violet-100 bg-gradient-to-r from-violet-50 to-indigo-50 px-5 py-4 flex items-start gap-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
          <Sparkles size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-bold text-violet-700 uppercase tracking-wider">AI Operational Insight</p>
            <span className="text-[10px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-medium">AI</span>
          </div>
          {aiLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <RefreshCw size={13} className="animate-spin" /> Analysing operational data…
            </div>
          ) : aiError ? (
            <p className="text-sm text-slate-400 italic">AI insights aren’t available right now</p>
          ) : aiInsight ? (
            <p className="text-sm text-slate-700 leading-relaxed">{aiInsight}</p>
          ) : null}
        </div>
        {!aiLoading && data && (
          <button onClick={() => fetchInsight({ shiftsToday: data.shiftsToday, pendingTimesheets, openIncidents, overdueInvoices, expiringDocs, activeClients, activeStaff, revenueThisMonth, healthScore })}
            className="p-1.5 rounded-lg text-violet-400 hover:text-violet-600 hover:bg-violet-100 transition-colors shrink-0">
            <RefreshCw size={13} />
          </button>
        )}
      </motion.div>

      {/* ── Shift Chart + Activity Feed ──────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Shift activity chart */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-bold text-slate-700">Shift Activity</p>
              <p className="text-xs text-slate-400 mt-0.5">Scheduled vs completed — last 7 days</p>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" />Scheduled</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Completed</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Cancelled</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <defs>
                <linearGradient id="gInd" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.15}/><stop offset="100%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gEm" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.15}/><stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="Scheduled" stroke="#6366f1" strokeWidth={2.5} fill="url(#gInd)" dot={false} activeDot={{ r:4, strokeWidth:0 }} />
              <Area type="monotone" dataKey="Completed" stroke="#10b981" strokeWidth={2.5} fill="url(#gEm)"  dot={false} activeDot={{ r:4, strokeWidth:0 }} />
              <Area type="monotone" dataKey="Cancelled" stroke="#ef4444" strokeWidth={1.5} fill="none" strokeDasharray="3 3" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Live alerts / activity feed */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
                Alerts &amp; Activity
                <span className="flex items-center gap-1 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                  <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />Live
                </span>
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Real-time notifications</p>
            </div>
          </div>
          <div className="flex-1 space-y-2.5 overflow-y-auto max-h-[190px] pr-1">
            {activityFeed.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs py-8">
                <Bell size={22} className="opacity-30 mb-2" />
                No recent activity
              </div>
            ) : activityFeed.map((a, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className={clsx('w-7 h-7 rounded-xl flex items-center justify-center shrink-0 mt-0.5',
                  a.type === 'incident' ? 'bg-red-50' : a.status === 'completed' ? 'bg-emerald-50' : 'bg-slate-50')}>
                  {a.type === 'incident'
                    ? <AlertCircle size={13} className="text-red-500" />
                    : a.status === 'completed'
                    ? <CheckCircle size={13} className="text-emerald-500" />
                    : <X size={13} className="text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <ActivityBadge type={a.type} status={a.status} />
                    <span className="text-[10px] text-slate-400">{a.ts ? format(new Date(a.ts), 'h:mm a') : ''}</span>
                  </div>
                  <p className="text-xs text-slate-600 truncate">
                    {a.type === 'incident' ? a.description : `${a.person || 'Staff'} — ${a.description}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── Today's Shift Breakdown + Quick Stats ────────────────────────────── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Shift status donut */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm font-bold text-slate-700 mb-1">Today's Shifts</p>
          <p className="text-xs text-slate-400 mb-3">Status distribution — {format(today, 'd MMM yyyy')}</p>
          <div className="flex items-center gap-4">
            <div className="shrink-0">
              <ResponsiveContainer width={110} height={110}>
                <PieChart>
                  <Pie data={shiftDonut.length ? shiftDonut : [{ name:'None',value:1,color:'#e2e8f0' }]}
                    cx="50%" cy="50%" innerRadius={32} outerRadius={48} dataKey="value" paddingAngle={2}>
                    {(shiftDonut.length ? shiftDonut : [{ color:'#e2e8f0' }]).map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {[
                { label:'Total',     val: st.total||0,     color:'text-slate-700' },
                { label:'Completed', val: st.completed||0, color:'text-emerald-600' },
                { label:'Booked',    val: st.booked||0,    color:'text-indigo-600' },
                { label:'Vacant',    val: st.vacant||0,    color:'text-amber-600' },
                { label:'Cancelled', val: st.cancelled||0, color:'text-red-500' },
              ].map(r => (
                <div key={r.label} className="flex justify-between text-xs">
                  <span className="text-slate-500">{r.label}</span>
                  <span className={clsx('font-bold', r.color)}>{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* This week quick stats */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm font-bold text-slate-700 mb-3">This Week</p>
          <div className="space-y-3">
            {[
              { label:'Shifts Scheduled', val: shiftsThisWeek || 0, icon: Calendar, color:'text-indigo-600', bg:'bg-indigo-50' },
              { label:'KM Driven',        val: `${parseFloat(kmWeek?.total_km||0).toFixed(0)} km`, icon: Navigation2, color:'text-cyan-600', bg:'bg-cyan-50' },
              { label:'Leave Pending',    val: leavePending, icon: Clock, color:'text-amber-600', bg:'bg-amber-50' },
              { label:'Expiring Docs',    val: expiringDocs, icon: FileWarning, color:'text-red-600', bg:'bg-red-50' },
            ].map(r => (
              <div key={r.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={clsx('w-7 h-7 rounded-lg flex items-center justify-center', r.bg)}>
                    <r.icon size={13} className={r.color} />
                  </div>
                  <span className="text-xs text-slate-600">{r.label}</span>
                </div>
                <span className={clsx('text-sm font-bold', r.color)}>{r.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top workers this week */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm font-bold text-slate-700 mb-3">Top Workers This Week</p>
          <div className="space-y-3">
            {staffWorkload.slice(0,4).map((s, i) => (
              <div key={s.name} className="flex items-center gap-2.5">
                <Avatar name={s.name} i={i} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-700 truncate">{s.name}</p>
                  <p className="text-[10px] text-slate-400">{s.shifts} shifts · {s.hours}h</p>
                </div>
                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">#{i+1}</span>
              </div>
            ))}
            {staffWorkload.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No shift data this week</p>}
          </div>
        </div>
      </motion.div>

      {/* ── Overall Health & Compliance ──────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <ShieldCheck size={15} className="text-indigo-500" />
              Overall Health &amp; Compliance
            </p>
            <p className="text-xs text-slate-400 mt-0.5">Current state snapshot — shifts, notes, incidents &amp; payroll</p>
          </div>
          <Link to="/reports/hub" className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-1">
            Full Report <ChevronRight size={11} />
          </Link>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Gauge */}
            <div className="flex flex-col items-center justify-center">
              <Gauge value={healthScore.overall || 0} size={140} strokeWidth={12} />
              <p className="text-sm font-semibold text-slate-700 mt-3">
                {healthScore.overall >= 70 ? 'Healthy' : healthScore.overall >= 40 ? 'Needs Attention' : 'Critical'}
              </p>
              <p className="text-xs text-slate-400">Overall Health Score</p>
            </div>

            {/* Score breakdown */}
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                <div>
                  <HealthBar label="Shift Compliance"    pct={healthScore.shiftComp   || 0} target={80} />
                  <HealthBar label="Client Compliance"   pct={healthScore.clientComp  || 0} target={70} />
                  <HealthBar label="Incident Resolution" pct={healthScore.incidentRes || 0} target={60} />
                </div>
                <div>
                  <HealthBar label="Shift Notes Filed"   pct={healthScore.notesFiled  || 0} target={90} />
                  <HealthBar label="Payroll Health"      pct={healthScore.payrollHealth||0} target={70} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Health sub-panels */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 divide-x divide-y md:divide-y-0 divide-slate-100 border-t border-slate-100">
          {[
            {
              label: 'Staff Onboarding', pct: healthScore.shiftComp || 0,
              icon: UserPlus, color: 'text-blue-600',
              rows: [
                ['Total staff',        staffOnboarding.total || 0],
                ['With start date',    staffOnboarding.with_start_date || 0],
                ['With documents',     staffOnboarding.with_docs || 0],
                ['Expiring docs',      expiringDocs],
              ],
              to: '/staff',
            },
            {
              label: 'Client Compliance', pct: healthScore.clientComp || 0,
              icon: Users, color: 'text-emerald-600',
              rows: [
                ['Active clients',   clientStats.total_active || 0],
                ['With NDIS number', clientStats.with_ndis || 0],
                ['With documents',   clientDocsCount],
                ['New this month',   clientStats.new_this_month || 0],
              ],
              to: '/clients',
            },
            {
              label: 'Shifts', pct: healthScore.shiftComp || 0,
              icon: Calendar, color: 'text-indigo-600',
              rows: [
                ['Total today',   st.total || 0],
                ['Completed',     st.completed || 0],
                ['Booked',        st.booked || 0],
                ['Vacant',        st.vacant || 0],
              ],
              to: '/scheduler',
            },
            {
              label: 'Incidents', pct: healthScore.incidentRes || 0,
              icon: AlertTriangle, color: 'text-red-600',
              rows: [
                ['Total',        totalInc],
                ['Open',         incMap.open || 0],
                ['In progress',  incMap.in_progress || 0],
                ['Closed',       incMap.closed || 0],
              ],
              to: '/incidents',
            },
            {
              label: 'Shift Notes', pct: healthScore.notesFiled || 0,
              icon: ClipboardCheck, color: 'text-violet-600',
              rows: [
                ['Completed shifts',  totalCompletedShifts],
                ['Notes filed',       withNotesCount],
                ['Notes missing',     Math.max(0, totalCompletedShifts - withNotesCount)],
                ['Coverage',          totalCompletedShifts > 0 ? `${Math.round(withNotesCount/totalCompletedShifts*100)}%` : '—'],
              ],
              to: '/scheduler',
            },
            {
              label: 'Payroll Health', pct: healthScore.payrollHealth || 0,
              icon: CreditCard, color: 'text-amber-600',
              rows: [
                ['Paid invoices',  invoiceHealth.paid || 0],
                ['Sent (pending)', invoiceHealth.sent || 0],
                ['Draft',         invoiceHealth.draft || 0],
                ['Overdue',       invoiceHealth.overdue || 0],
              ],
              to: '/invoices',
            },
          ].map((panel, pi) => (
            <Link key={pi} to={panel.to} className="p-4 hover:bg-slate-50 transition-colors block">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <panel.icon size={13} className={panel.color} />
                  <p className="text-[11px] font-bold text-slate-600">{panel.label}</p>
                </div>
                <span className="text-[11px] font-black"
                  style={{ color: panel.pct >= 70 ? '#10b981' : panel.pct >= 40 ? '#f59e0b' : '#ef4444' }}>
                  {panel.pct}%
                </span>
              </div>
              <div className="h-1 bg-slate-100 rounded-full mb-3 overflow-hidden">
                <div className="h-1 rounded-full transition-all"
                  style={{ width:`${panel.pct}%`, background: panel.pct>=70?'#10b981':panel.pct>=40?'#f59e0b':'#ef4444' }} />
              </div>
              <div className="space-y-1">
                {panel.rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[11px]">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-semibold text-slate-700">{v}</span>
                  </div>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </motion.div>

      {/* ── Recent Clients + Birthdays ───────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Recent clients */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <UserPlus size={14} className="text-emerald-500" />Recent Clients
            </p>
            <Link to="/clients" className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-1">
              View all <ChevronRight size={11} />
            </Link>
          </div>
          <div className="space-y-2.5">
            {newClients.length === 0
              ? <p className="text-xs text-slate-400 text-center py-6">No clients yet</p>
              : newClients.map((c, i) => (
              <div key={c.id} className="flex items-center gap-3">
                <Avatar name={`${c.first_name} ${c.last_name}`} i={i} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-700">{c.first_name} {c.last_name}</p>
                  <p className="text-[10px] text-slate-400">{c.ndis_number ? `NDIS: ${c.ndis_number}` : c.client_type?.replace(/_/g,' ') || '—'}</p>
                </div>
                <span className="text-[10px] text-slate-400 shrink-0">{c.created_at ? format(new Date(c.created_at), 'dd MMM') : ''}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Birthdays */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <p className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
            <Cake size={14} className="text-pink-500" />Upcoming Birthdays
          </p>
          <p className="text-xs text-slate-400 mb-4">Staff birthdays this month</p>
          {birthdaysThisMonth.length === 0 ? (
            <div className="flex flex-col items-center py-6 text-slate-400">
              <Cake size={24} className="opacity-20 mb-2" />
              <p className="text-xs">No birthdays this month</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {birthdaysThisMonth.map((s, i) => {
                const dob = new Date(s.date_of_birth);
                const thisYear = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
                const daysLeft = differenceInDays(thisYear, today);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <Avatar name={`${s.first_name} ${s.last_name}`} i={i} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700">{s.first_name} {s.last_name}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{s.role?.replace(/_/g,' ')}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold text-pink-600">{format(dob, 'dd MMM')}</p>
                      <p className="text-[10px] text-slate-400">
                        {daysLeft === 0 ? 'Today! 🎂' : daysLeft < 0 ? 'Passed' : `in ${daysLeft}d`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Expiring Docs + Upcoming Reviews ────────────────────────────────── */}
      {(expiringClientDocs.length > 0 || upcomingReviews?.length > 0) && (
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {expiringClientDocs.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <p className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2">
                <FileWarning size={14} />Client Documents Expiring Soon
              </p>
              <div className="space-y-2">
                {expiringClientDocs.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-white rounded-xl px-3 py-2">
                    <div>
                      <span className="font-semibold text-slate-700">{d.client_name}</span>
                      <span className="text-slate-400 ml-2">{d.name}</span>
                    </div>
                    <span className="text-amber-600 font-bold shrink-0">
                      {d.expiry_date ? format(new Date(d.expiry_date), 'dd MMM') : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {upcomingReviews?.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
              <p className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                <ClipboardCheck size={14} />Client Reviews Due
              </p>
              <div className="space-y-2">
                {upcomingReviews.map((r, i) => (
                  <div key={r.id} className="flex items-center justify-between text-xs bg-white rounded-xl px-3 py-2">
                    <span className="font-semibold text-slate-700">{r.first_name} {r.last_name}</span>
                    <span className="text-blue-600 font-bold">
                      {r.next_review_date ? format(new Date(r.next_review_date), 'dd MMM') : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Recent Shifts + Financial Summary ───────────────────────────────── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Left — Recent Shifts */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <p className="text-sm font-bold text-slate-700">Recent Shifts</p>
              <p className="text-xs text-slate-400 mt-0.5">Last 3 days · with financials</p>
            </div>
            <Link to="/scheduler" className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-1">
              View all <ChevronRight size={11} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Date','Client','Worker','Status','Charge','Profit'].map(h => (
                    <th key={h} className={`px-3 py-3 text-[10px] uppercase tracking-wide text-slate-400 font-semibold ${['Charge','Profit'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentShifts?.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-400">No recent shifts</td></tr>
                ) : recentShifts?.slice(0, 8).map((s, i) => {
                  const charge = parseFloat(s.total_client_charge || 0);
                  const profit = parseFloat(s.shift_profit || 0);
                  const noRate = s.missing_client_rate || s.missing_staff_rate;
                  const fmt = (n) => n > 0 ? `$${n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
                  return (
                    <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap">{format(parseISO(s.shift_date), 'dd MMM')}</td>
                      <td className="px-3 py-2.5 max-w-[80px]">
                        <div className="flex items-center gap-1.5">
                          <Avatar name={s.client_names || '?'} i={i} size={5} />
                          <span className="text-slate-600 truncate text-[11px]">{s.client_names?.split(' ')[0] || '—'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        {s.staff_name
                          ? <div className="flex items-center gap-1.5"><Avatar name={s.staff_name} i={i+2} size={5} /><span className="text-slate-500 text-[11px]">{s.staff_name?.split(' ')[0]}</span></div>
                          : <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">Vacant</span>}
                      </td>
                      <td className="px-3 py-2.5"><StatusBadge status={s.status} /></td>
                      <td className="px-3 py-2.5 text-right">
                        {noRate
                          ? <span className="text-[10px] text-amber-500 font-bold">No rate</span>
                          : <span className="font-semibold text-indigo-600">{fmt(charge)}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {noRate ? <span className="text-slate-300">—</span>
                          : <span className={`font-bold ${profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(profit)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {recentShifts?.length > 0 && (() => {
                const totCharge = recentShifts.reduce((s, r) => s + parseFloat(r.total_client_charge || 0), 0);
                const totProfit = recentShifts.reduce((s, r) => s + parseFloat(r.shift_profit || 0), 0);
                const fmt = (n) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                return (
                  <tfoot>
                    <tr className="bg-slate-50 border-t border-slate-200">
                      <td colSpan={4} className="px-3 py-2 text-[11px] font-semibold text-slate-400">
                        {recentShifts.length} shifts
                      </td>
                      <td className="px-3 py-2 text-right text-[11px] font-black text-indigo-600">{fmt(totCharge)}</td>
                      <td className="px-3 py-2 text-right text-[11px] font-black text-emerald-600">{fmt(totProfit)}</td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>
        </div>

        {/* Right — Financial Dashboard Summary */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <DollarSign size={14} className="text-violet-500" />Financial Summary
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Month to date · {format(today, 'MMMM yyyy')}</p>
            </div>
            <Link to="/financials" className="text-xs text-violet-600 font-semibold hover:underline flex items-center gap-1">
              Full report <ChevronRight size={11} />
            </Link>
          </div>

          <div className="p-5 flex-1 flex flex-col gap-4">
            {/* Top 3 fin stats */}
            {(() => {
              const rev    = parseFloat(finSummary.revenue || 0);
              const cost   = parseFloat(finSummary.staff_cost || 0);
              const sup    = parseFloat(finSummary.super || 0);
              const profit = parseFloat(finSummary.gross_profit || 0);
              const margin = rev > 0 ? Math.round((profit / rev) * 100) : 0;
              const lastRev = parseFloat(finLastMonth.revenue || 0);
              const revDiff = lastRev > 0 ? Math.round(((rev - lastRev) / lastRev) * 100) : null;
              const fmtD = (n) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

              return (
                <>
                  {/* Stat row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
                      <p className="text-[10px] text-slate-500 mb-1">MTD Revenue</p>
                      <p className="text-lg font-black text-indigo-600">{fmtD(rev)}</p>
                      {revDiff !== null && (
                        <p className={`text-[10px] flex items-center gap-0.5 mt-0.5 font-semibold ${revDiff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {revDiff >= 0 ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                          {Math.abs(revDiff)}% vs last month
                        </p>
                      )}
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                      <p className="text-[10px] text-slate-500 mb-1">Gross Profit</p>
                      <p className="text-lg font-black text-emerald-600">{fmtD(profit)}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{margin}% margin</p>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <p className="text-[10px] text-slate-500 mb-1">Staff Cost</p>
                      <p className="text-lg font-black text-red-500">{fmtD(cost)}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">excl. super</p>
                    </div>
                    <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3">
                      <p className="text-[10px] text-slate-500 mb-1">Superannuation</p>
                      <p className="text-lg font-black text-violet-600">{fmtD(sup)}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">11.5% guarantee</p>
                    </div>
                  </div>

                  {/* Margin bar */}
                  <div>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-slate-500 font-medium">Profit Margin</span>
                      <span className={`font-bold ${margin >= 30 ? 'text-emerald-600' : margin >= 15 ? 'text-amber-600' : 'text-red-500'}`}>{margin}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div className="h-2 rounded-full"
                        style={{ background: margin >= 30 ? '#10b981' : margin >= 15 ? '#f59e0b' : '#ef4444' }}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(margin, 100)}%` }}
                        transition={{ duration: .9, ease: [.4,0,.2,1] }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                      <span>{finSummary.completed_shifts || 0} completed shifts</span>
                      <span>avg {fmtD(parseFloat(finSummary.avg_charge || 0))}/shift</span>
                    </div>
                  </div>

                  {/* Missing rates warning */}
                  {parseInt(finSummary.missing_rates || 0) > 0 && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                      <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                      <p className="text-xs text-amber-700">
                        <strong>{finSummary.missing_rates}</strong> shifts missing rates — revenue may be understated.{' '}
                        <Link to="/financials/config" className="underline font-semibold">Fix now</Link>
                      </p>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Top clients by revenue */}
            {topClients.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Top Clients YTD</p>
                <div className="space-y-2">
                  {topClients.map((c, i) => {
                    const maxRev = parseFloat(topClients[0]?.revenue || 1);
                    const pct    = Math.round((parseFloat(c.revenue) / maxRev) * 100);
                    return (
                      <div key={i} className="flex items-center gap-2.5">
                        <Avatar name={c.name} i={i} size={6} />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-xs mb-0.5">
                            <span className="text-slate-600 font-medium truncate">{c.name}</span>
                            <span className="font-bold text-indigo-600 shrink-0 ml-2">
                              ${parseFloat(c.revenue).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          </div>
                          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-1 rounded-full bg-indigo-400" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{c.shifts} shifts</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>


      {/* ── AI Intelligence Center ───────────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50 via-indigo-50 to-slate-50 p-5 shadow-sm">

        {/* Section header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-sm">AI Intelligence Center</h2>
              <p className="text-xs text-slate-500">Proactive insights from Rostery AI</p>
            </div>
          </div>
          <button
            onClick={runAiAnalysis}
            disabled={aiCenterLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 shadow-md transition-opacity"
          >
            {aiCenterLoading
              ? <><Loader2 size={14} className="animate-spin" /> Analysing…</>
              : <><Sparkles size={14} /> Run AI Analysis</>}
          </button>
        </div>

        {/* 4 insight cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

          {/* Card A — Staff Burnout Risk */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shrink-0">
                <Flame size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Staff Burnout Risk</p>
                <p className="text-[10px] text-slate-400">High-workload staff alerts</p>
              </div>
            </div>
            <div className="flex-1">
              {!aiInsights && !aiCenterLoading && (
                <p className="text-xs text-slate-400 italic">Click "Run AI Analysis" to load insights.</p>
              )}
              {aiCenterLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 size={12} className="animate-spin" /> Analysing staff data…
                </div>
              )}
              {aiInsights && !aiCenterLoading && aiInsights.burnoutError && (
                <p className="text-xs text-red-400 italic">Analysis unavailable</p>
              )}
              {aiInsights && !aiCenterLoading && !aiInsights.burnoutError && (
                <>
                  {(!aiInsights.burnout || aiInsights.burnout.length === 0) ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-600">
                      <CheckCircle size={13} /> No burnout risks detected
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {aiInsights.burnout.slice(0, 5).map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          {s.staffId ? (
                            <Link to={`/staff/${s.staffId}`} className="text-slate-700 font-medium truncate flex-1 min-w-0 mr-2 hover:text-primary-700 hover:underline">
                              {s.staffName || 'Staff member'}
                            </Link>
                          ) : (
                            <span className="text-slate-700 font-medium truncate flex-1 min-w-0 mr-2">{s.staffName || 'Staff member'}</span>
                          )}
                          <span className={clsx(
                            'shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full capitalize',
                            s.riskLevel === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          )}>
                            {s.riskLevel || 'at risk'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Card B — Fund Depletion Alerts */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
                <TrendingDown size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Fund Depletion Alerts</p>
                <p className="text-[10px] text-slate-400">Plans &gt;80% utilised (30 days)</p>
              </div>
            </div>
            <div className="flex-1">
              {!aiInsights && !aiCenterLoading && (
                <p className="text-xs text-slate-400 italic">Click "Run AI Analysis" to load insights.</p>
              )}
              {aiCenterLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 size={12} className="animate-spin" /> Checking funding levels…
                </div>
              )}
              {aiInsights && !aiCenterLoading && aiInsights.fundingError && (
                <p className="text-xs text-red-400 italic">Analysis unavailable</p>
              )}
              {aiInsights && !aiCenterLoading && !aiInsights.fundingError && (
                <>
                  {aiInsights.funding.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-600">
                      <CheckCircle size={13} /> All funds within safe limits
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {aiInsights.funding.slice(0, 5).map((f, i) => {
                        const spent = parseFloat(f.spent_amount || 0);
                        const total = parseFloat(f.fund_amount  || 1);
                        const pct   = Math.round((spent / total) * 100);
                        return (
                          <div key={i} className="text-xs">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-slate-700 font-medium truncate flex-1 min-w-0 mr-2">
                                {f.client_name || f.client || 'Client'}
                              </span>
                              <span className="shrink-0 font-bold text-amber-600">{pct}%</span>
                            </div>
                            <p className="text-[10px] text-slate-400 truncate">{f.fund_name || f.funding_type || 'NDIS Fund'}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Card C — Client Deterioration Signals */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shrink-0">
                <HeartPulse size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Client Deterioration</p>
                <p className="text-[10px] text-slate-400">Early warning signals</p>
              </div>
            </div>
            <div className="flex-1">
              {!aiInsights && !aiCenterLoading && (
                <p className="text-xs text-slate-400 italic">Click "Run AI Analysis" to load insights.</p>
              )}
              {aiCenterLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 size={12} className="animate-spin" /> Scanning client signals…
                </div>
              )}
              {aiInsights && !aiCenterLoading && aiInsights.deteriorationError && (
                <p className="text-xs text-red-400 italic">Analysis unavailable</p>
              )}
              {aiInsights && !aiCenterLoading && !aiInsights.deteriorationError && (
                <>
                  {(!aiInsights.deterioration || aiInsights.deterioration.length === 0) ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-600">
                      <CheckCircle size={13} /> No deterioration signals detected
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {aiInsights.deterioration.slice(0, 4).map((c, i) => (
                        <div key={i} className="text-xs">
                          {c.clientId ? (
                            <Link to={`/clients/${c.clientId}`} className="block text-slate-700 font-medium truncate hover:text-primary-700 hover:underline">
                              {c.clientName || 'Client'}
                            </Link>
                          ) : (
                            <p className="text-slate-700 font-medium truncate">{c.clientName || 'Client'}</p>
                          )}
                          <p className="text-[10px] text-slate-400 truncate">
                            {c.summary || c.signals?.[0] || 'Flagged for review'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Card D — AI Auto-Roster (static) */}
          <div className="bg-white rounded-2xl border border-indigo-100 p-5 shadow-sm flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
                <CalendarRange size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">AI Auto-Roster</p>
                <p className="text-[10px] text-slate-400">Smart scheduling assistant</p>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500 leading-relaxed mb-4">
                Use AI to automatically roster the coming week based on staff skills, availability and client needs.
              </p>
            </div>
            <Link
              to="/scheduler"
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-semibold hover:opacity-90 transition-opacity shadow-sm"
            >
              <CalendarRange size={13} /> Generate Roster
            </Link>
          </div>

        </div>
      </motion.div>

    </motion.div>
  );
}
